'use client';

import Link from 'next/link';
import { useRouter } from 'next/navigation';
import type {
  ComponentProps,
  FocusEventHandler,
  MouseEventHandler,
} from 'react';

const prefetchedJobDetailRoutes = new Set<string>();
const MAX_PREFETCHED_JOB_DETAIL_ROUTES = 100;

function rememberPrefetchedRoute(href: string) {
  prefetchedJobDetailRoutes.add(href);

  if (prefetchedJobDetailRoutes.size <= MAX_PREFETCHED_JOB_DETAIL_ROUTES) {
    return;
  }

  const oldestRoute = prefetchedJobDetailRoutes.values().next().value;

  if (oldestRoute !== undefined) prefetchedJobDetailRoutes.delete(oldestRoute);
}

type JobDetailLinkProps = Omit<
  ComponentProps<typeof Link>,
  'href' | 'prefetch'
> & {
  href: string;
};

export function JobDetailLink({
  href,
  onFocus,
  onMouseEnter,
  ...props
}: JobDetailLinkProps) {
  const router = useRouter();

  const prefetchOnce = () => {
    if (prefetchedJobDetailRoutes.has(href)) {
      return;
    }

    rememberPrefetchedRoute(href);
    router.prefetch(href);
  };

  const handleFocus: FocusEventHandler<HTMLAnchorElement> = event => {
    onFocus?.(event);
    prefetchOnce();
  };

  const handleMouseEnter: MouseEventHandler<HTMLAnchorElement> = event => {
    onMouseEnter?.(event);
    prefetchOnce();
  };

  return (
    <Link
      {...props}
      href={href}
      prefetch={false}
      onFocus={handleFocus}
      onMouseEnter={handleMouseEnter}
    />
  );
}
